import React from "react";
import { AdminImportCard } from "Main/Admin/AdminImportCard";
import { AdminImportStyle } from "Main/Admin/AdminImportStyle";
import { normalizeHeader, parseDateOnly, parseRows } from "Main/Admin/AdminImportCsvUtils";
import { CsvImportMessageTexts, CsvImportStatusTexts, defaultCsvImportStatusTexts, getRequestStatusTitle, getValidationStatusTitle, RequestStatus, sortImportedRowsByDate, toLocalDate, useCsvImportState } from "Main/Admin/AdminImportShared";
import { Icon } from "Shared/Components/Icon/Style";
import { useApi } from "Shared/Context/API";
import useLang from "Shared/Context/LangContext";
import { Desk } from "Shared/Functions/Desk";

export interface DeskImportRow {
    date: string;
    deskId: string;
    isValid: boolean;
    invalidReason?: string;
    sortDate?: string;
    requestStatus: RequestStatus;
    requestMessage?: string;
}

export const DeskCsvImport = () => {
    const api = useApi();
    const { curLangData } = useLang();
    const [showAllRows, setShowAllRows] = React.useState(false);
    const {
        rows,
        setRows,
        fileName,
        message,
        error,
        setMessage,
        setError,
        isBatchRunning,
        hasBatchExecuted,
        validRowCount,
        resetForNewFile,
        executeBatch,
    } = useCsvImportState<DeskImportRow>();

    const statusTexts = React.useMemo<CsvImportStatusTexts>(() => ({
        invalidRow: curLangData?.text.CsvImportStatusInvalidRow ?? defaultCsvImportStatusTexts.invalidRow,
        validRow: curLangData?.text.CsvImportStatusValidRow ?? defaultCsvImportStatusTexts.validRow,
        notExecutedBecauseValidationFailed: curLangData?.text.CsvImportStatusNotExecutedBecauseValidationFailed ?? defaultCsvImportStatusTexts.notExecutedBecauseValidationFailed,
        uploadedSuccessfully: curLangData?.text.CsvImportStatusUploadedSuccessfully ?? defaultCsvImportStatusTexts.uploadedSuccessfully,
        uploadFailed: curLangData?.text.CsvImportStatusUploadFailed ?? defaultCsvImportStatusTexts.uploadFailed,
        notExecutedYet: curLangData?.text.CsvImportStatusNotExecutedYet ?? defaultCsvImportStatusTexts.notExecutedYet,
    }), [curLangData?.text.CsvImportStatusInvalidRow, curLangData?.text.CsvImportStatusNotExecutedBecauseValidationFailed, curLangData?.text.CsvImportStatusNotExecutedYet, curLangData?.text.CsvImportStatusUploadFailed, curLangData?.text.CsvImportStatusUploadedSuccessfully, curLangData?.text.CsvImportStatusValidRow]);

    const messageTexts = React.useMemo<CsvImportMessageTexts>(() => ({
        noValidRows: curLangData?.error.CsvImportNoValidRows ?? "No valid rows available for upload.",
        uploadedSuccessfully: curLangData?.text.CsvImportStatusUploadedSuccessfully ?? "Uploaded successfully.",
        requestFailed: curLangData?.error.CsvImportRequestFailed ?? "Request failed.",
        formatStartUpload: (count, itemLabel) => {
            const template = curLangData?.info.CsvImportStartUpload ?? "Starting upload for {count} {itemLabel} row(s)...";
            return template
                .replace("{count}", count.toString())
                .replace("{itemLabel}", itemLabel);
        },
        formatFinishedUpload: (itemLabel, successCount, failedCount) => {
            const template = curLangData?.info.CsvImportFinishedUpload ?? "{itemLabel} upload finished: {success} succeeded, {failed} failed.";
            const defaultItemLabel = `${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)}`;
            return template
                .replace("{itemLabel}", defaultItemLabel)
                .replace("{success}", successCount.toString())
                .replace("{failed}", failedCount.toString());
        },
    }), [curLangData?.error.CsvImportNoValidRows, curLangData?.error.CsvImportRequestFailed, curLangData?.info.CsvImportFinishedUpload, curLangData?.info.CsvImportStartUpload, curLangData?.text.CsvImportStatusUploadedSuccessfully]);

    const onFileImport = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        resetForNewFile(file.name);

        const content = await file.text();
        const rows = parseRows(content);
        const importedRows: DeskImportRow[] = [];
        let invalidRows = 0;
        const seenBookings = new Set<string>();

        rows.forEach((row, rowIndex) => {
            const rawDate = (row.values[0] ?? "").trim();
            const rawDeskId = (row.values[1] ?? "").trim();

            if (rowIndex === 0) {
                const first = normalizeHeader(rawDate);
                const second = normalizeHeader(rawDeskId);
                if (first === "date" && (second === "deskid" || second === "desk")) {
                    return;
                }
            }

            const parsedDate = parseDateOnly(rawDate);
            let invalidReason: string | undefined;
            if (!parsedDate) {
                invalidReason = curLangData?.error.CsvImportInvalidDateFormat ?? "Invalid date format.";
            } else if (rawDeskId.length === 0) {
                invalidReason = curLangData?.error.CsvImportDeskIdMissing ?? "DeskId is missing.";
            }

            let isValid = !invalidReason;

            if (isValid && parsedDate) {
                const duplicateKey = `${parsedDate}|${rawDeskId.toLowerCase()}`;
                if (seenBookings.has(duplicateKey)) {
                    isValid = false;
                    invalidReason = curLangData?.error.CsvImportDeskDuplicateEntry ?? "Duplicated entry for this Desk on this date.";
                } else {
                    seenBookings.add(duplicateKey);
                }
            }

            if (!isValid) {
                invalidRows++;
            }

            importedRows.push({
                date: parsedDate ?? rawDate,
                deskId: rawDeskId,
                isValid,
                invalidReason,
                sortDate: parsedDate,
                requestStatus: "idle",
                requestMessage: undefined,
            });
        });

        setRows(sortImportedRowsByDate(importedRows));

        if (importedRows.length === 0) {
            setError(curLangData?.error.CsvImportDeskNoRowsFound ?? "No rows found. Expected columns: date, deskId.");
        } else {
            const validRows = importedRows.length - invalidRows;
            const template = curLangData?.info.CsvImportDeskLoadedRows ?? "Loaded {total} desk row(s): {valid} valid, {invalid} invalid.";
            setMessage(template
                .replace("{total}", importedRows.length.toString())
                .replace("{valid}", validRows.toString())
                .replace("{invalid}", invalidRows.toString()));
        }

        event.target.value = "";
    }, [curLangData?.error.CsvImportDeskDuplicateEntry, curLangData?.error.CsvImportDeskIdMissing, curLangData?.error.CsvImportDeskNoRowsFound, curLangData?.error.CsvImportInvalidDateFormat, curLangData?.info.CsvImportDeskLoadedRows, resetForNewFile, setError, setMessage, setRows]);

    const onBatchExecute = React.useCallback(async () => {
        await executeBatch(
            "desk",
            async (row) => {
                let localDate = toLocalDate(row.sortDate);
                let nextDay = new Date(localDate);
                nextDay.setDate(localDate.getDate() + 1);
                await api.Admin.blockSeat(new Desk(row.deskId), localDate, nextDay);
            },
            messageTexts,
        );
    }, [api.Admin, executeBatch, messageTexts]);

    const visibleRows = React.useMemo(
        () => showAllRows ? rows : rows.filter((row) => !row.isValid || row.requestStatus === "error"),
        [rows, showAllRows]
    );

    return (
        <AdminImportCard<DeskImportRow>
            title={curLangData?.text.CsvImportDeskTitle ?? "Desk CSV Import"}
            description={curLangData?.text.CsvImportDeskDescription ?? "Expected columns: Date (dateonly), DeskId."}
            uploadButtonText={curLangData?.text.CsvImportDeskUploadButton ?? "Upload Desk CSV"}
            fileName={fileName}
            message={message}
            error={error}
            emptyText={showAllRows
                ? (curLangData?.text.CsvImportDeskEmptyAll ?? "No desk CSV data loaded yet.")
                : (curLangData?.text.CsvImportDeskEmptyInvalid ?? "No invalid or failed desk rows to show.")}
            headers={[
                curLangData?.text.CsvImportHeaderValidation ?? "Validation",
                curLangData?.text.CsvImportHeaderRequest ?? "Request",
                curLangData?.text.CsvImportHeaderDate ?? "Date",
                curLangData?.text.CsvImportHeaderDeskId ?? "DeskId",
            ]}
            rows={visibleRows}
            rowKey={(row, index) => `${row.date}-${row.deskId}-${row.isValid}-${row.requestStatus}-${index}`}
            renderRowCells={(row) => (
                <>
                    <AdminImportStyle.ValidityCell isValid={row.isValid && row.requestStatus !== "error"}>
                        <AdminImportStyle.ValidityIconWrap title={getValidationStatusTitle(row, statusTexts)}>
                            <Icon.PfeilRechts isBold size={"20px"} />
                        </AdminImportStyle.ValidityIconWrap>
                    </AdminImportStyle.ValidityCell>
                    <AdminImportStyle.RequestStatusCell status={row.requestStatus}>
                        <AdminImportStyle.ValidityIconWrap title={getRequestStatusTitle(row, statusTexts)}>
                            {row.requestStatus === "success" && <Icon.Haken isBold size={"20px"} />}
                            {row.requestStatus === "error" && <Icon.X isBold size={"20px"} />}
                            {row.requestStatus === "idle" && <Icon.PfeilRechts isBold size={"20px"} />}
                        </AdminImportStyle.ValidityIconWrap>
                    </AdminImportStyle.RequestStatusCell>
                    <AdminImportStyle.TableCell>{row.date}</AdminImportStyle.TableCell>
                    <AdminImportStyle.TableCell>{row.deskId}</AdminImportStyle.TableCell>
                </>
            )}
            onFileImport={onFileImport}
            batchButtonText={curLangData?.text.CsvImportDeskExecuteUpload ?? "Execute Desk Upload"}
            uploadingText={curLangData?.text.CsvImportUploading ?? "Uploading..."}
            onBatchExecute={onBatchExecute}
            isBatchRunning={isBatchRunning}
            isBatchDisabled={hasBatchExecuted || validRowCount === 0}
            controls={(
                <AdminImportStyle.ControlsRow>
                    <AdminImportStyle.CheckboxLabel>
                        <AdminImportStyle.CheckboxInput
                            type={"checkbox"}
                            checked={showAllRows}
                            onChange={(event) => setShowAllRows(event.target.checked)}
                        />
                        {curLangData?.text.CsvImportShowAllRows ?? "Show all rows"}
                    </AdminImportStyle.CheckboxLabel>
                </AdminImportStyle.ControlsRow>
            )}
        />
    );
};
